import React, { useState, useCallback, useEffect, useRef } from "react";
import { StyleSheet, Platform } from "react-native";
import { web } from "./Platform";
import CustomTextInput from "./CustomTextInput"; // Importing CustomTextInput

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const AddressAutocomplete = ({
  value = "",
  onChange,
  onAddressSelect,
  placeholder = "Enter address",
  className = "inputBox",
  containerClassName = "",
  disabled = false,
  required = false,
  name = "address",
  label = "Address",
  validate = false,
  forceValidation = false,
}) => {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const lastSelectedValue = useRef(value); // Track the last selected value
  const isSelecting = useRef(false); // Track if a selection is in progress
  const hasUserModified = useRef(false); // Track if user has manually modified the input
  const [addressData, setAddressData] = useState([]);

  // // Update input value when prop changes without triggering suggestions
  // useEffect(() => {
  //   // Ensure value is a string to prevent [object Object]
  //   if (value === "[object Object]") value = "";
  //   const newValue = typeof value === "string" ? value : "";
  //   if (
  //     newValue !== inputValue &&
  //     !isSelecting.current &&
  //     !hasUserModified.current
  //   ) {
  //     setInputValue(newValue);
  //     setShowSuggestions(false); // Prevent suggestions on external value change
  //   }
  // }, [value, inputValue]);

  useEffect(() => {
    // Handle object-type value from parent
    let newValue = "";
    if (value === "[object Object]") value = "";
    if (typeof value === "string") {
      newValue = value;
    } else if (value && typeof value === "object" && value.fullAddress) {
      newValue = value.fullAddress;
    }
    // Prevent clearing if lastSelectedValue exists and new value is empty
    if (
      newValue !== inputValue &&
      !isSelecting.current &&
      !hasUserModified.current &&
      (newValue || !lastSelectedValue.current) // Only update if newValue is non-empty or no lastSelectedValue
    ) {
      setInputValue(newValue);
      lastSelectedValue.current = newValue;
      setShowSuggestions(false);
      // Debug log to track value changes
      console.log(
        "useEffect: value=",
        value,
        "newValue=",
        newValue,
        "inputValue=",
        inputValue
      );
    }
  }, [value, inputValue]);

  useEffect(() => {
    if (
      addressData &&
      typeof addressData.fullAddress === "string" &&
      addressData.fullAddress.trim().length > 0
    ) {
      setTimeout(() => {
        onAddressSelect(addressData);
      }, 1000);
    }
  }, [addressData]);

  // Geoapify API search function
  const searchAddress = async (query) => {
    if (!isFocused || query.length < 3 || isSelecting.current) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoadingAddress(true);

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          query
        )}&country=us&limit=5&apiKey=55820a932513458d855d3f9b840a5e8c`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch addresses");

      const data = await response.json();
      console.log(data);
      // Format the suggestions
      const formattedSuggestions = data.features.map((item) => ({
        display_name: item.properties.formatted,
        lat: item.properties.lat,
        lon: item.properties.lon,
        address: {
          house_number: item.properties.housenumber || "",
          road: item.properties.street || "",
          city:
            item.properties.city ||
            item.properties.town ||
            item.properties.village ||
            "",
          state: item.properties.state || "",
          postcode: item.properties.postcode || "",
        },
      }));

      setAddressSuggestions(formattedSuggestions);
      setShowSuggestions(formattedSuggestions.length > 0 && isFocused);
    } catch (error) {
      console.error("Address search error:", error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Create debounced version
  const debouncedSearch = useCallback(debounce(searchAddress, 500), [
    isFocused,
  ]);

  // Handle input change
  const handleAddressInputChange = (text) => {
    setInputValue(text);
    hasUserModified.current = true; // Mark as user-modified
    if (!isSelecting.current) {
      // Call parent onChange if provided
      if (onChange) {
        const syntheticEvent = {
          target: { name, value: text },
        };
        onChange(syntheticEvent);
      }
    }

    // Trigger address search
    debouncedSearch(text);
    setSelectedAddressIndex(-1);
  };

  // Handle address selection
  const handleAddressSelect = (suggestion) => {
    isSelecting.current = true;
    hasUserModified.current = false; // Reset user modification flag on selection
    console.log("Selected suggestion:", suggestion); // Debug log to inspect suggestion structure
    if (!suggestion.address) return;
    // Format the address, ensuring it's a string
    const formattedAddress =
      `${suggestion.address.house_number || ""} ${
        suggestion.address.road || ""
      }`.trim() || suggestion.display_name;

    // Create address data object
    const addressData = {
      fullAddress: formattedAddress,
      streetAddress: formattedAddress,
      city: suggestion.address.city,
      state: suggestion.address.state,
      zipCode: suggestion.address.postcode,
      lat: suggestion.lat,
      lon: suggestion.lon,
      displayName: suggestion.display_name,
    };

    // Update input value with the formatted address string
    setInputValue(formattedAddress);
    lastSelectedValue.current = formattedAddress; // Store the last selected value

    // Call parent callback if provided
    if (onChange) {
      const syntheticEvent = {
        target: { name, value: formattedAddress },
      };
      onChange(syntheticEvent);
    }

    // Hide suggestions and clear state completely, then manage focus
    setShowSuggestions(false);
    setAddressSuggestions([]);
    setSelectedAddressIndex(-1);
    setIsFocused(false); // Prevent focus from re-triggering
    isSelecting.current = false; // Reset selection flag

    if (onAddressSelect) {
      onAddressSelect(addressData);
      setAddressData(addressData);
    }
    // Delay blur to ensure state updates complete
    // setTimeout(() => {
    //   const inputElement = document.activeElement;
    //   if (inputElement && inputElement.blur) {
    //     inputElement.blur();
    //   }
    // }, 1000); // Small delay to allow state to settle
  };

  // Handle keyboard navigation
  const handleAddressKeyDown = (e) => {
    //if (!showSuggestions || addressSuggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedAddressIndex((prev) =>
          prev < addressSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedAddressIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedAddressIndex >= 0) {
          handleAddressSelect(addressSuggestions[selectedAddressIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedAddressIndex(-1);
        break;
      case "Tab":
        setShowSuggestions(false);
        handleAddressSelect(inputValue);

        break;
    }
  };

  return (
    <div
      className={`inputBoxContainer ${containerClassName}`}
      style={styles.inputBoxContainer}
    >
      {label && (
        <span className="inputBoxLabel" style={styles.inputBoxLabel}>
          {label}
        </span>
      )}
      <CustomTextInput
        value={inputValue}
        onChangeText={handleAddressInputChange}
        onKeyPress={handleAddressKeyDown}
        onBlur={() => {
          setShowSuggestions(false);
          setIsFocused(false);
          if (
            !inputValue &&
            lastSelectedValue.current &&
            !hasUserModified.current
          ) {
            if (lastSelectedValue.current && !inputValue) {
              setInputValue(lastSelectedValue.current);
              if (onChange) {
                const syntheticEvent = {
                  target: { name, value: lastSelectedValue.current },
                };
                onChange(syntheticEvent);
              }
            }
          }
          // Retain last selected value only if input is cleared and no recent user modification
          //   if (
          //     !inputValue &&
          //     lastSelectedValue.current &&
          //     !hasUserModified.current
          //   ) {
          //     setTimeout(() => {
          //       setInputValue(lastSelectedValue.current);
          //       if (onChange) {
          //         const syntheticEvent = {
          //           target: { name, value: lastSelectedValue.current },
          //         };
          //         onChange(syntheticEvent);
          //       }
          //     }, 100); // Delay to avoid transient empty state
          //   }
          hasUserModified.current = false; // Reset on blur if no new input
        }}
        onFocus={() => {
          setIsFocused(true);
          // Suggestions are handled by debouncedSearch, not onFocus
        }}
        placeholder={placeholder}
        className={className}
        style={styles.inputBoxOverride}
        disabled={disabled}
        required={required}
        name={name}
        validate={validate}
        forceValidation={forceValidation}
        autoCapitalize="none"
        autoComplete="off"
        placeholderTextColor="#999"
      />

      {/* Loading indicator */}
      {isLoadingAddress && (
        <div style={styles.loadingContainer}>
          <span style={styles.attributionText}>Loading...</span>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && addressSuggestions.length > 0 && (
        <div style={styles.suggestionsContainer}>
          {addressSuggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleAddressSelect(suggestion)}
              style={{
                ...styles.suggestionItem,
                ...(selectedAddressIndex === index
                  ? styles.selectedSuggestion
                  : {}),
                ...(index === addressSuggestions.length - 1
                  ? styles.lastSuggestion
                  : {}),
              }}
              onMouseEnter={() => setSelectedAddressIndex(index)}
            >
              <span style={styles.suggestionText}>
                {suggestion.display_name}
              </span>
            </div>
          ))}
          <div style={styles.attributionContainer}>
            <span style={styles.attributionText}>Powered by Geoapify</span>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  inputBoxContainer: {
    flexDirection: "row",
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    marginBottom: 20,

    position: "relative",
    backgroundColor: "#fff",
    width: "95%",
    border: "1px solid rgb(192, 192, 192)",
  },
  inputBoxLabel: {
    position: "absolute",
    backgroundColor: "#fff",
    top: -12,
    left: 3,
    fontSize: 14,
    color: "gray",
    paddingHorizontal: 3,
    zIndex: 1,
    fontWeight: "bold",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  inputBoxOverride: {
    // Override to ensure CustomTextInput fits within the container
    width: "100%",
    borderWidth: 0,
    height: "34px",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderRadius: "5px",
    color: "rgb(81, 87, 93)",
    padding: "5px",
  },
  inputBoxFocus: {
    borderColor: "#66afe9",
    boxShadow: "inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)",
    WebkitBoxShadow:
      "inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    borderRadius: "5px",
    color: "rgb(81, 87, 93)",
    padding: "5px",
  },
  loadingContainer: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: [{ translateY: -10 }],
  },
  suggestionsContainer: {
    position: "sticky",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#cccccc",
    borderTopWidth: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    maxHeight: 250,
    zIndex: 10000,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    overflowY: "auto",
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  lastSuggestion: {
    borderBottomWidth: 0,
  },
  selectedSuggestion: {
    backgroundColor: "#f0f0f0",
  },
  suggestionText: {
    fontSize: 15,
    color: "#333333",
    lineHeight: 2,
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  attributionContainer: {
    padding: 6,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    backgroundColor: "#f9f9f9",
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  attributionText: {
    fontSize: 10,
    color: "#666",
  },
});

export default AddressAutocomplete;
